import { Point, TargetLayer, TargetLayerIngredient } from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  Grid,
  GridDTO,
  GridIngredientDTO,
  GridLayerDTO,
  IngredientStockDTO,
  LayerDTO,
  LayerTypeDTO,
  WellIngDTO,
} from 'business/models/Models';
import {
  Round,
  LayerType,
  getIngredientColor,
  getIngredientTypeColor,
  isNullOrUndefined,
  getIngredientsByIngredientStockId,
  getIngredientStockByDict,
  getIngredientIngredientTypeByIngIngTypeIdDict,
} from 'utils/Global';
import { getLinkedLayerGridDto } from '../GenerateUtil';
import {
  CreateTargetLayerIngredient,
  findWellsInLayer,
  IsBuffer,
  updateWellByTargetLayer,
  createTargetLayer,
  getIngredientType,
  updateDropByTargetLayer,
  RemoveWellIngredientsForOverrideLayer,
} from './GenerateCommon';
import { Compute } from './VolCalculator';
import { VOLUME_DECIMAL_PLACE_PRECISION } from './constants';

export function GridLayerGenerate(docData: ExperimentDtoClient, lyr: LayerDTO) {
  let gridLayer: GridLayerDTO;
  if (lyr.type != LayerType.Grid) {
    throw new Error('GridLayerGenerate: Invalid Layer Type.');
  } else {
    gridLayer = lyr.layerDetail as GridLayerDTO;
  }

  const layerWidth: number = lyr.width + 1;
  const layerHeight: number = lyr.height + 1;
  const gridLayerRow = gridLayer;
  const gridRow: GridDTO = getLinkedLayerGridDto(docData.layers, gridLayerRow);
  const gridIngredients: GridIngredientDTO[] = gridRow.gridIngredient;
  const gridLayerRows: GridLayerDTO[] = GetAllGridLayerLinked(docData, gridLayer);
  const gridVaryAlongRowID: number = gridRow.wellVolumeVaryAlong;
  const gridRowGridLayerRowCount: number = gridLayerRows.length;
  const gridLayerRowSubGridNumber: number = gridLayerRow.subGridNumber;

  //ToDo : fix it?
  const wells: WellIngDTO[][] = findWellsInLayer(
    docData.plateDto,
    lyr,
    docData.containerRows,
    docData.containerCols,
  );

  for (let i = 0; i < layerHeight; i++) {
    for (let j = 0; j < layerWidth; j++) {
      if (lyr.overrideLayer) {
        RemoveWellIngredientsForOverrideLayer(docData, lyr, wells[i][j]);
      }

      //ToDo - DB model:
      const wellLayerVolume: number = ComputeVaryAlong(
        gridRow,
        gridRowGridLayerRowCount,
        layerWidth,
        layerHeight,
        gridLayerRowSubGridNumber,
        new Point(j, i),
        gridRow.startWellVolume,
        gridRow.stopWellVolume,
        gridVaryAlongRowID,
      );

      //ToDo: Need to remove this code - well volume will be computed by some other piece of code

      const targetLayer: TargetLayer = createTargetLayer(lyr, wells[i][j]);
      targetLayer.volume = wellLayerVolume;

      GenerateWellLayerIngredients(
        gridLayerRow,
        gridRow,
        gridRowGridLayerRowCount,
        gridLayerRowSubGridNumber,
        layerWidth,
        layerHeight,
        wells[i][j],
        new Point(j, i),
        targetLayer,
        gridIngredients,
      );

      Compute(targetLayer, lyr, docData);
      if (lyr.targetDrop < 0) {
        updateWellByTargetLayer(lyr, targetLayer, wells[i][j]);
        wells[i][j].wellWaterVolume += targetLayer.waterVolume;
      } else {
        const drop = updateDropByTargetLayer(lyr, targetLayer, wells[i][j]);
        if (drop) {
          drop.volume += targetLayer.volume;
          drop.waterVolume += targetLayer.waterVolume;
        }
      }
    }
  }

  RemoveZeroConcentrationZeroPHIngredients(docData, lyr);
}

function RemoveZeroConcentrationZeroPHIngredients(
  docData: ExperimentDtoClient,
  layerRow: LayerDTO,
) {}

function ComputeVaryAlong(
  grid: Grid,
  gridRowGridLayerRowCount: number,
  layerWidth: number,
  layerHeight: number,
  subGridNum: number,
  location: Point,
  start: number,
  stop: number,
  varyAlongID: number,
): number {
  switch (varyAlongID) {
    case 1:
      return start;
    case 2:
      if (layerWidth == 1) {
        return start;
      }
      return parseFloat(
        (start + ((stop - start) / (layerWidth - 1)) * location.X).toFixed(
          VOLUME_DECIMAL_PLACE_PRECISION,
        ),
      );
    case 3:
      if (layerHeight == 1) {
        return start;
      }
      return start + ((stop - start) / (layerHeight - 1)) * location.Y;
    case 4:
      if (gridRowGridLayerRowCount == 1) {
        return start;
      }
      return start + ((stop - start) / (gridRowGridLayerRowCount - 1)) * (subGridNum - 1);
    case 5:
      if (layerHeight * layerWidth * gridRowGridLayerRowCount == 1) {
        return start;
      }
      return (
        start +
        ((stop - start) / (layerHeight * layerWidth * gridRowGridLayerRowCount - 1)) *
          (layerWidth * layerHeight * (subGridNum - 1) +
            location.Y * layerWidth +
            location.X)
      );
    case 6:
      if (layerWidth == 1) {
        return start;
      }
      return start + ((stop - start) / (layerWidth - 1)) * location.X;
    default:
      throw new Error('Unknown grid vary encountered.');
  }
}

function GenerateWellLayerIngredients(
  gridLayer: GridLayerDTO,
  gridRow: GridDTO,
  gridRowGridLayerRowCount: number,
  gridLayerSubGridNumber: number,
  layerWidth: number,
  layerHeight: number,
  wellRow: WellIngDTO,
  wellLocation: Point,
  targetLayer: TargetLayer,
  gridIngredients: GridIngredientDTO[],
) {
  let concentration = 0;
  const grid: GridDTO = gridRow;

  // add each ingredient in the grid to the well
  for (const gridIngredient of gridIngredients) {
    // skip over new ingredients where ing ing type hasn't been selected
    if (gridIngredient.ingredientIngredientTypeId == null) {
      continue;
    }
    const targetLayerIngredient: TargetLayerIngredient =
      CreateTargetLayerIngredient(targetLayer);
    targetLayerIngredient.concentration = 0;

    targetLayerIngredient.ingredientIngredientTypeId =
      gridIngredient.ingredientIngredientTypeId;
    targetLayerIngredient.ingredientStockIngredient = getIngredientsByIngredientStockId(
      gridIngredient.ingredientStockId,
    )?.ingredient;
    targetLayerIngredient.color = getIngredientColor(
      gridIngredient.ingredientIngredientTypeId,
    );
    //--Todo - Need to verify hex color converter
    targetLayerIngredient.hexColor =
      '#' + targetLayerIngredient.color.toString(16).padStart(6, '0').toUpperCase();
    const ingIngType = getIngredientIngredientTypeByIngIngTypeIdDict(
      gridIngredient.ingredientIngredientTypeId,
    );
    targetLayerIngredient.ingredientTypeId = ingIngType.ingredientTypeId;
    const ingType = getIngredientType(ingIngType.ingredientTypeId);
    const ingTypeColor = getIngredientTypeColor(ingType.ingredientTypeColorId);
    targetLayerIngredient.ingredientTypeColorGroupId = ingTypeColor.groupId;
    targetLayerIngredient.ingredientTypeName = ingType.name;

    targetLayerIngredient.isBuffer = IsBuffer(gridIngredient.ingredientIngredientTypeId);
    targetLayerIngredient.ingredientTypeIsBuffer = targetLayerIngredient.isBuffer;
    targetLayerIngredient.ingredientIngredientTypeIsBuffer = false;

    targetLayerIngredient.ingredientStockId = gridIngredient.ingredientStockId;
    targetLayerIngredient.ingredientStock = getIngredientStockByDict(
      gridIngredient.ingredientStockId,
    );
    targetLayerIngredient.preMixedConcentration = 0;
    const gridIngredientConcentrationVaryAlong: number =
      gridIngredient.concentrationVaryAlong;

    //ToDo - DB model:
    //If Custom is selected, then read concentration from the Cell in file (if exists) otherwise read by normal method.

    if (gridIngredientConcentrationVaryAlong == 6) {
      try {
        concentration =
          gridIngredient.concentrationVaryAlongCustom.concentrationVaryAlongCustomValues[
            wellLocation.Y
          ][wellLocation.X];
        if (isNullOrUndefined(concentration)) {
          concentration = 0;
        }
      } catch {
        concentration = 0;
      }
    } else {
      concentration = ComputeVaryAlong(
        grid,
        gridRowGridLayerRowCount,
        layerWidth,
        layerHeight,
        gridLayerSubGridNumber,
        wellLocation,
        gridIngredient.startConcentration,
        gridIngredient.stopConcentration,
        gridIngredientConcentrationVaryAlong,
      );
    }

    if (targetLayerIngredient.preMixedConcentration != concentration) {
      targetLayerIngredient.preMixedConcentration = concentration;
    }

    if (IsBuffer(gridIngredient.ingredientIngredientTypeId)) {
      let pH = 0;
      if (gridIngredient.pHvaryAlong != null && gridIngredient.pHvaryAlong == 6) {
        try {
          pH =
            gridIngredient.pHVaryAlongCustom.pHVaryAlongCustomValues[wellLocation.Y][
              wellLocation.X
            ];
        } catch {
          pH = 0;
        }
      } else {
        // If Stop pH is not set and is null and pH vary along is set to None
        // set stop pH value equal to start pH value to facilite computing pH vary along
        let stoppH = 0;
        if (
          gridIngredient.stoppH == null &&
          (gridIngredient.pHvaryAlong || gridIngredient.pHvaryAlong == 1)
        ) {
          stoppH = gridIngredient.startpH;
        } else {
          stoppH = gridIngredient.stoppH;
        }

        pH = ComputeVaryAlong(
          grid,
          gridRowGridLayerRowCount,
          layerWidth,
          layerHeight,
          gridLayerSubGridNumber,
          wellLocation,
          gridIngredient.startpH,
          stoppH,
          gridIngredient.pHvaryAlong,
        );
      }

      if (targetLayerIngredient.pH == null || targetLayerIngredient.pH != pH) {
        targetLayerIngredient.pH = pH;
      }

      // May be converting a well ingredient row from a single vial buffer to a dual vial.
      // Need to update the row, but only dirty it if absolutely necessary.
      if (gridIngredient.highPhingredientStockId == null) {
        if (targetLayerIngredient.highPhIngStockId != null) {
          targetLayerIngredient.highPhIngStockId = null;
        }
      } else {
        if (
          targetLayerIngredient.highPhIngStockId == null ||
          targetLayerIngredient.highPhIngStockId != gridIngredient.highPhingredientStockId
        ) {
          targetLayerIngredient.highPhIngStockId = gridIngredient.highPhingredientStockId;
          targetLayerIngredient.highPhIngStock = getIngredientStockByDict(
            gridIngredient.highPhingredientStockId,
          );
        }
      }
    }

    const lowVolStock: IngredientStockDTO = getIngredientStockByDict(
      targetLayerIngredient.ingredientStockId,
    );
    const stockConcentration: number = lowVolStock.stockConcentration;

    targetLayerIngredient.stockConcentration = stockConcentration;

    // Calculate volume for the ingredient - wellIngredient
    const volume: number = Round(
      targetLayer.volume * (concentration / stockConcentration),
    );
    targetLayerIngredient.volume = volume;
  }
}

export function GetAllGridLayerLinked(
  docData: ExperimentDtoClient,
  gridLayer: GridLayerDTO,
): GridLayerDTO[] {
  const linkedGridLayers: GridLayerDTO[] = [];
  const allGridLayers = docData.layers.filter((l) => l.type === LayerTypeDTO.Grid);
  for (const l of allGridLayers) {
    const gl = l.layerDetail as GridLayerDTO;
    if (gl.gridId == gridLayer.gridId) {
      linkedGridLayers.push(gl);
    }
  }
  return linkedGridLayers;
}

export function countDecimals(number: number, beforeDecimal = false): number {
  const parts = number.toString().split('.');
  if (beforeDecimal) {
    return parts[0].length;
  }
  if (parts.length <= 1) {
    return 0;
  }
  return parts[1].length;
}
